/**
 * AI Research Assistant Component v3
 * 
 * Truth-bound AI research interface with clear separation between:
 * - Internal knowledge (ThreadLock features from Live Truth)
 * - External knowledge (general legal/procedural information)
 * 
 * KEY FEATURES:
 * - Mode parameter: 'internal' | 'external' | 'hybrid'
 * - Injects Truth data into AI prompts
 * - Route and role context awareness
 * - Clear visual distinction between knowledge sources
 * - "No answer if not in truth" enforcement for internal queries
 */

import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Sparkles, BookOpen, Globe, AlertCircle, Copy, X, Loader2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import type { TruthRoute, TruthCapability } from '@/hooks/useTruthData';

interface AiResearchAssistantProps {
  role: 'srl' | 'pro' | 'admin';
  searchQuery: string;
  researchMode: 'internal' | 'external' | 'hybrid';
  currentRoute: TruthRoute | null;
  routeCapabilities: TruthCapability[];
}

interface HelpResearchRequest {
  role: 'srl' | 'pro' | 'admin';
  query: string;
  mode: 'internal' | 'external' | 'hybrid';
  context?: {
    currentRoute?: {
      path: string;
      component: string;
      navReachability: string;
    };
    capabilities?: Array<{
      key: string;
      claim: string;
      exposure: string;
    }>;
  };
}

interface HelpResearchResponse {
  internalAnswer?: string;
  externalAnswer?: string;
  sources: Array<{
    type: 'internal' | 'external';
    content: string;
  }>;
  modelUsed: string;
  requestId: string;
  latencyMs: number;
}

export function AiResearchAssistant({
  role,
  searchQuery,
  researchMode,
  currentRoute,
  routeCapabilities,
}: AiResearchAssistantProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [response, setResponse] = useState<HelpResearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastQuery, setLastQuery] = useState('');

  // Auto-research when query changes (debounced)
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery === lastQuery) return;

    const timer = setTimeout(() => {
      handleResearch();
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [searchQuery, researchMode]);

  const handleResearch = async () => {
    if (!searchQuery.trim()) return;

    setIsGenerating(true);
    setError(null);
    setLastQuery(searchQuery);

    try {
      const helpResearch = httpsCallable<HelpResearchRequest, HelpResearchResponse>(
        functions,
        'helpResearch'
      );

      const context: HelpResearchRequest['context'] = {};
      
      if (currentRoute) {
        context.currentRoute = {
          path: currentRoute.path,
          component: currentRoute.component,
          navReachability: currentRoute.navReachability,
        };
      }

      if (routeCapabilities.length > 0) {
        context.capabilities = routeCapabilities.map(cap => ({
          key: cap.key,
          claim: cap.claim,
          exposure: cap.exposure,
        }));
      }

      const result = await helpResearch({
        role,
        query: searchQuery.trim(),
        mode: researchMode,
        context,
      });

      if (!result.data) {
        throw new Error('No response from research service');
      }

      setResponse(result.data);
    } catch (err: unknown) {
      console.error('[AiResearchAssistant] Error:', err);

      let errorMessage = 'An unexpected error occurred';
      let debugId: string | undefined;

      if (err && typeof err === 'object' && 'code' in err) {
        const firebaseError = err as { code: string; message: string; details?: { requestId?: string; debugId?: string } };

        // Extract debugId if available
        if (firebaseError.details) {
          debugId = firebaseError.details.requestId || firebaseError.details.debugId;
        }

        if (firebaseError.code === 'functions/unauthenticated') {
          errorMessage = 'Please sign in to use the research assistant.';
        } else if (firebaseError.code === 'functions/resource-exhausted') {
          errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
        } else if (firebaseError.code === 'functions/deadline-exceeded') {
          errorMessage = 'Request timed out. Please try a shorter question or try again later.';
        } else if (firebaseError.code === 'functions/internal') {
          errorMessage = `Internal server error. ${debugId ? `Reference: ${debugId}` : ''}`;
        } else {
          errorMessage = firebaseError.message || 'Failed to generate research response';
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      // Log to console for debugging (dev only)
      if (debugId) {
        console.error(`[AiResearchAssistant] Error Reference ID: ${debugId}`);
      }

      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add toast notification here
  };

  const handleDismiss = () => {
    setResponse(null);
    setError(null);
    setLastQuery('');
  };

  if (!searchQuery.trim() && !response) {
    return (
      <div className="text-center py-16 text-surface-muted">
        <Sparkles className="w-12 h-12 mx-auto mb-4 text-surface-muted opacity-50" />
        <p className="text-lg">Enter a question above to start researching</p>
        <p className="text-sm mt-2">Choose a research mode to focus your search</p>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="bg-surface-panel border border-surface-default rounded-lg p-8">
        <div className="flex items-center justify-center gap-3 text-surface-muted">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Researching your question...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-950/40 border border-red-700/50 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-300">Research Error</p>
            <p className="text-red-400 mt-1">{error}</p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-red-400 hover:text-red-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  if (!response) return null;

  return (
    <div className="space-y-6">
      {/* Internal Answer Section */}
      {response.internalAnswer && (researchMode === 'internal' || researchMode === 'hybrid') && (
        <div className="bg-slate-800 border border-brand-orange rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-brand-orange/20 to-brand-orange/10 px-6 py-4 border-b border-brand-orange/30">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-brand-orange" />
              <div className="flex-1">
                <h3 className="font-semibold text-white">About ThreadLock</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Sourced from ThreadLock Platform Documentation
                </p>
              </div>
              <button
                onClick={() => handleCopy(response.internalAnswer!)}
                className="p-2 hover:bg-brand-orange/10 rounded transition-colors"
                title="Copy to clipboard"
              >
                <Copy className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
          <div className="px-6 py-5">
            <div className="prose prose-sm prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  a: ({ ...props }) => <a {...props} className="text-orange-400 underline" target="_blank" rel="noopener noreferrer" />,
                  code: ({ children, ...props }) => <code {...props} className="bg-slate-700 text-orange-300 px-1 py-0.5 rounded text-sm font-mono">{children}</code>,
                  strong: ({ ...props }) => <strong {...props} className="text-slate-100 font-semibold" />,
                  ul: ({ ...props }) => <ul {...props} className="list-disc pl-5 space-y-1 text-slate-300" />,
                  ol: ({ ...props }) => <ol {...props} className="list-decimal pl-5 space-y-1 text-slate-300" />,
                  li: ({ ...props }) => <li {...props} className="text-slate-300" />,
                  p: ({ ...props }) => <p {...props} className="text-slate-200 mb-3 last:mb-0" />,
                }}
              >
                {response.internalAnswer}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* External Answer Section */}
      {response.externalAnswer && (researchMode === 'external' || researchMode === 'hybrid') && (
        <div className="bg-slate-800 border border-blue-500/50 rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-900/40 to-blue-900/20 px-6 py-4 border-b border-blue-500/30">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-blue-400" />
              <div className="flex-1">
                <h3 className="font-semibold text-white">General Information</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  External source • Educational only — not legal advice
                </p>
              </div>
              <button
                onClick={() => handleCopy(response.externalAnswer!)}
                className="p-2 hover:bg-blue-500/10 rounded transition-colors"
                title="Copy to clipboard"
              >
                <Copy className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
          <div className="px-6 py-5">
            <div className="prose prose-sm prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  a: ({ ...props }) => <a {...props} className="text-blue-400 underline" target="_blank" rel="noopener noreferrer" />,
                  code: ({ children, ...props }) => <code {...props} className="bg-slate-700 text-blue-300 px-1 py-0.5 rounded text-sm font-mono">{children}</code>,
                  strong: ({ ...props }) => <strong {...props} className="text-slate-100 font-semibold" />,
                  ul: ({ ...props }) => <ul {...props} className="list-disc pl-5 space-y-1 text-slate-300" />,
                  ol: ({ ...props }) => <ol {...props} className="list-decimal pl-5 space-y-1 text-slate-300" />,
                  li: ({ ...props }) => <li {...props} className="text-slate-300" />,
                  p: ({ ...props }) => <p {...props} className="text-slate-200 mb-3 last:mb-0" />,
                }}
              >
                {response.externalAnswer}
              </ReactMarkdown>
            </div>
          </div>
          {/* External Disclaimer */}
          <div className="px-6 py-3 bg-amber-900/30 border-t border-amber-700/40">
            <p className="text-xs text-amber-300">
              ⚠️ This information is for educational purposes only and is not specific to ThreadLock.
              It should not be relied upon as legal advice.
            </p>
          </div>
        </div>
      )}

      {/* Metadata Footer */}
      <div className="flex items-center justify-between text-xs text-slate-400 px-2">
        <span>Model: {response.modelUsed}</span>
        <span>Response time: {response.latencyMs}ms</span>
        <button
          onClick={handleDismiss}
          className="text-surface-muted hover:text-surface-secondary transition-colors"
        >
          Clear results
        </button>
      </div>
    </div>
  );
}
